import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

export interface AppNotification {
  id: number;
  title: string;
  body: string | null;
  category: string;
  url: string | null;
  read_at: string | null;
  created_at: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  /** Nur ungelesene Benachrichtigungen (Backend liefert ausschließlich diese). */
  readonly notifications = signal<AppNotification[]>([]);
  readonly unreadCount = computed(() => this.notifications().length);

  private eventSource?: EventSource;
  private started = false;

  /** Einmalig beim ersten Header-Mount starten: Liste laden + SSE-Stream. */
  start() {
    if (this.started) return;
    this.started = true;
    this.loadAll();
    this.connectStream();
  }

  stop() {
    this.eventSource?.close();
    this.eventSource = undefined;
    this.started = false;
    this.notifications.set([]);
  }

  loadAll() {
    this.http.get<AppNotification[]>(`${environment.apiUrl}/notifications`)
      .subscribe({ next: list => this.notifications.set(list), error: () => {} });
  }

  markRead(id: number) {
    // Optimistisch aus der Liste entfernen, dann serverseitig markieren.
    this.notifications.update(list => list.filter(n => n.id !== id));
    this.http.patch(`${environment.apiUrl}/notifications/${id}/read`, {}).subscribe({ error: () => {} });
  }

  markAllRead() {
    this.notifications.set([]);
    this.http.post(`${environment.apiUrl}/notifications/read-all`, {}).subscribe({ error: () => {} });
  }

  private connectStream() {
    const token = this.auth.getToken();
    if (!token || typeof EventSource === 'undefined') return;
    this.eventSource = new EventSource(
      `${environment.apiUrl}/notifications/stream?token=${encodeURIComponent(token)}`
    );
    this.eventSource.addEventListener('refresh', () => this.loadAll());
  }
}
